interface Props {
  ministry: string;
}

// Simple SVG marks per ministry. Read at body-text size — these are
// inline labels next to the ministry name, not feature icons.
//
// Matching is substring/case-insensitive: OLG's real MSP ministry
// names carry a "Minister" suffix or slot/time detail ("Weekday
// Lector", "Communion Minister - 3:Sunday 9:00am") that an exact
// match would miss entirely (Hospitality Minister and Weekday Lector
// both silently fell back to the generic mark before this). A role
// this doesn't recognize falls back to a generic person rather than
// guessing at a shape.
export default function MinistryIcon({ ministry }: Props) {
  const key = ministry.toLowerCase();
  if (key.includes("sacristan")) return <Flame />;
  if (key.includes("lector")) return <Book />;
  if (key.includes("communion") || key === "emhc") return <Chalice />;
  if (key.includes("hospitality")) return <Hand />;
  if (key.includes("usher")) return <Door />;
  return <Person />;
}

const ICON_PROPS = {
  className: "ministry-icon",
  viewBox: "0 0 16 16",
  width: 16,
  height: 16,
  "aria-hidden": true as const,
};

// Communion Minister / EMHC: a chalice — wide bowl narrowing to a
// stem and base, sized to match the other icons' visual mass.
function Chalice() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3.4 2 H12.6 L11.3 7.4 a3.3 3.3 0 0 1 -6.6 0 Z" fill="currentColor" />
      <rect x="7.3" y="10.2" width="1.4" height="2.8" fill="currentColor" />
      <path d="M4.4 14.6 H11.6 L10.6 13 H5.4 Z" fill="currentColor" />
    </svg>
  );
}

// Lector: solid open book, sized to match the chalice's visual
// mass. Pages are filled silhouettes; spine reads as a thin
// parchment cut-out (same negative-space technique as the chalice's
// base). Rotated 180° so the spine sits at the bottom and the pages
// open upward — the natural open-book orientation.
function Book() {
  return (
    <svg {...ICON_PROPS}>
      <g transform="rotate(180 8 8)">
        <path d="M8 2 L1.2 3.2 V13.6 L8 12.4 Z" fill="currentColor" />
        <path d="M8 2 L14.8 3.2 V13.6 L8 12.4 Z" fill="currentColor" />
        <line
          x1="8"
          y1="2.6"
          x2="8"
          y2="12.6"
          stroke="var(--parchment)"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}

// Sacristan: solid altar candle in a saucer holder, scaled up to
// match the chalice's visual mass.
function Flame() {
  return (
    <svg {...ICON_PROPS}>
      {/* flame: pointed top, rounded belly */}
      <path
        d="M8 0.6 c-1.9 1.6 -2.7 4.0 0 5.8 c2.7 -1.8 1.9 -4.2 0 -5.8 z"
        fill="currentColor"
      />
      {/* wick */}
      <line
        x1="8"
        y1="6.4"
        x2="8"
        y2="7.6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      {/* candle body: pillar */}
      <rect x="5.4" y="7.6" width="5.2" height="5.0" rx="0.4" fill="currentColor" />
      {/* holder saucer */}
      <path d="M3.6 12.6 H12.4 L11.4 14.6 H4.6 Z" fill="currentColor" />
    </svg>
  );
}

// Usher: pair of arched church doors with a gap between them,
// sized to match the EMHC host's visual mass. Each leaf has its
// handle on the inside edge facing the other, which is also how
// real double doors work.
function Door() {
  return (
    <svg {...ICON_PROPS}>
      {/* left door leaf */}
      <path d="M1.4 14.6 V4 a3.2 3.2 0 0 1 6 0 V14.6 Z" fill="currentColor" />
      {/* right door leaf */}
      <path d="M8.6 14.6 V4 a3.2 3.2 0 0 1 6 0 V14.6 Z" fill="currentColor" />
      {/* handles on the inside edges */}
      <circle cx="6" cy="9.5" r="0.95" fill="var(--parchment)" />
      <circle cx="10" cy="9.5" r="0.95" fill="var(--parchment)" />
    </svg>
  );
}

// Hospitality: solid steaming mug. Handle drawn as a filled
// crescent rather than a stroked arc so it matches the rest.
function Hand() {
  return (
    <svg {...ICON_PROPS}>
      {/* steam wisps — narrow physical features, stay strokes */}
      <path
        d="M6 4 c0.6 -1 -0.6 -2 0 -3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M8.5 4 c0.6 -1 -0.6 -2 0 -3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      {/* mug body */}
      <path
        d="M3.2 6 H10.4 V11.2 a2.4 2.4 0 0 1 -2.4 2.4 H5.6 a2.4 2.4 0 0 1 -2.4 -2.4 Z"
        fill="currentColor"
      />
      {/* handle as a filled crescent */}
      <path
        d="M10.4 7.4 Q13.4 9.6 10.4 11.8 Q12 9.6 10.4 7.4 Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Fallback for any role we don't recognize yet: a generic person
// (head + shoulders), matching the shape already used for the
// Volunteers entry point elsewhere in the app.
function Person() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="8" cy="5.4" r="2.6" fill="currentColor" />
      <path d="M2.7 14.4 a5.3 5.3 0 0 1 10.6 0 z" fill="currentColor" />
    </svg>
  );
}
